import React, { useEffect, useRef } from 'react';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { AUTH_TOKEN } from '../config';
import useAvatarVoiceChat from '../hooks/useAvatarVoiceChat';
import { useRideGate } from '../hooks/useRideGate';
import { api } from '../services/api';
import { INTRO_RIDE } from '../worldRide';

// AvatarChatDock — a PERSISTENT quick-speak mic. The mic lives in ONE fixed
// spot (bottom-center) whenever there's someone to talk to, so users build
// muscle-memory: see the character, tap the mic, talk. The caption only
// appears while a turn is in flight, so idle it's just a lone, unobtrusive mic.
//
// It's deliberately split from the walk controls: the caption sits up top
// (non-interactive, a look-drag passes through) and the mic sits in the gap
// between the left joystick and the right-side music controls — so you can
// still move and look mid-conversation. Must live OUTSIDE the Canvas.

const RESIDENT = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('resident') === '1'
  && new URLSearchParams(window.location.search).get('city') === 'model';
const REBEL = { avatarId: 26, name: 'Rebel Rhythm' }; // the street resident POC

const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;

const pill = {
  position: 'fixed', top: `calc(${safe('top', 16)} + 46px)`, left: '50%', transform: 'translateX(-50%)',
  zIndex: 36, maxWidth: 'min(460px, 82vw)', textAlign: 'center', pointerEvents: 'none',
  background: 'rgba(10,8,12,0.82)', border: '1px solid rgba(255,178,94,0.5)', borderRadius: 14,
  padding: '8px 32px 9px 16px', color: '#ffe9c2', font: '14px system-ui, sans-serif',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
};
const closeBtn = {
  position: 'absolute', top: 4, right: 6, pointerEvents: 'auto',
  background: 'transparent', color: '#ffd27d', border: 'none',
  font: '600 17px system-ui', cursor: 'pointer', lineHeight: 1, padding: 4,
};
const nameLine = { fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 2 };

// Matches the HUD's music-toggle chrome: dark translucent, thin amber border,
// muted amber icon. State reads through the BORDER + a faint ring, never a
// bright fill — listening tints toward the neon red, busy states just dim.
const micStyle = (state) => {
  const busy = state === 'thinking' || state === 'connecting';
  const live = state === 'listening';
  return {
    position: 'fixed', left: '50%', bottom: safe('bottom', 20), transform: 'translateX(-50%)',
    zIndex: 37, width: 54, height: 54, borderRadius: '50%', display: 'grid', placeItems: 'center',
    touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
    pointerEvents: busy ? 'none' : 'auto', cursor: busy ? 'default' : 'pointer',
    background: 'rgba(20,12,6,0.65)',
    border: `1px solid ${live ? 'rgba(255,94,120,0.7)' : 'rgba(255,178,94,0.35)'}`,
    boxShadow: live ? '0 0 0 4px rgba(255,94,120,0.12)' : 'none',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
    opacity: busy ? 0.55 : 1,
    transition: 'border-color .15s ease, box-shadow .15s ease, opacity .15s ease',
  };
};
const STATUS = { connecting: 'Connecting…', listening: 'Listening…', thinking: 'Thinking…', speaking: 'Speaking — talk over them to cut in' };

// Monochrome mic glyph (amber, or neon-red while listening) — no bright emoji.
function MicIcon({ color }) {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="21" />
      <line x1="8.5" y1="21" x2="15.5" y2="21" />
    </svg>
  );
}

function Dock({ target, explicit }) {
  const closeChat = useWorldStore((s) => s.closeChat);
  const { phase, caption, error, active, toggleTalk, end } = useAvatarVoiceChat({ avatarId: target.avatarId });
  const first = target.name?.split(' ')[0] || 'them';

  const hangUp = () => { end(); if (explicit) closeChat(); };

  // Caption only when something's happening — idle, it's just the lone mic.
  const showPill = !!error || !!caption || phase !== 'idle';
  const line = error || caption || STATUS[phase] || '';

  return (
    <>
      {showPill && (
        <div style={pill}>
          {(explicit || active) && <button style={closeBtn} onClick={hangUp} aria-label="End conversation">×</button>}
          <div style={nameLine}>{target.name}</div>
          <div style={{ color: error ? '#ff9a9a' : '#ffe9c2', fontSize: 14.5, lineHeight: 1.35 }}>{line}</div>
        </div>
      )}

      <button
        style={micStyle(phase)}
        onClick={toggleTalk}
        title={active ? `End the conversation with ${first}` : `Talk to ${first}`}
        aria-label={active ? `End the conversation with ${first}` : `Talk to ${first}`}
        aria-pressed={active}
      >
        <MicIcon color={phase === 'listening' ? '#ff8a9c' : '#ffd27d'} />
      </button>
    </>
  );
}

export default function AvatarChatDock() {
  const chatAvatar = useWorldStore((s) => s.chatAvatar);
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);

  // Who the mic talks to: an explicitly-clicked avatar wins; otherwise the
  // street resident (when present). Interiors keep their own video dock
  // (LiveChatDock), so we don't double up there unless one was clicked.
  const setMicVisible = useWorldStore((s) => s.setMicVisible);
  const roomHasVideoChat = zoom === ZOOM.INTERIOR && !!ROOMS[interior]?.liveChat;
  const streetDefault = zoom === ZOOM.STREET && RESIDENT ? REBEL : null;
  const target = chatAvatar || (roomHasVideoChat ? null : streetDefault);
  const show = !!target && !!AUTH_TOKEN;
  // Let the movement controls know whether the center mic is up (so they raise
  // above it only when it's actually there, and drop back when it's gone).
  useEffect(() => { setMicVisible(show); return () => setMicVisible(false); }, [show, setMicVisible]);

  // TAPPING SPOR1 *IS* THE PICKUP — the member half of what GuestChatDock
  // already does. Boarding belongs in the conversation, not on a magic spot
  // near the car; his scene opens with "It's open — get in," so the chat you
  // asked for IS the pickup. The kerb card stays for walk-ups.
  //
  // The one thing guests can hardcode and members cannot: WHICH ride. A guest
  // is a stranger by definition, so the interview is always right. A member
  // may be someone Spor1 already knows, and that is the server's call
  // (GET /api/dialogue/ride → the homecoming variant), never a client guess.
  // If the call fails the interview stands — meeting someone twice is a
  // smaller wrong than greeting a returning artist as a stranger... which is
  // why we ASK, and only fall back when we cannot.
  //
  // And it waits for the story to have STARTED: `rideOpen` is false until
  // Lyra's call has been answered (useRideGate — the same rule the beacon and
  // the kerb card use). Spor1 being the first person you must talk to makes
  // him the easiest thing to trigger by accident on spawn.
  //
  // Hooks must stay ABOVE the early return below (a hook under it crashed the
  // guest dock: "Rendered more hooks than during the previous render").
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const { rideOpen } = useRideGate();
  const boardedFor = useRef(null);

  useEffect(() => {
    const id = chatAvatar?.avatarId;
    if (!AUTH_TOKEN || !id || !myAvatarId || !rideOpen) return undefined;
    if (!/spor\s*1/i.test(chatAvatar.name || '')) return undefined;
    if (boardedFor.current === id) return undefined;         // one board per tap
    if (useWorldStore.getState().ride) return undefined;      // already in the car
    boardedFor.current = id;

    let alive = true;
    (async () => {
      let key = 'act2_ride_spor1';
      let title = "Spor1's ride";
      try {
        const d = await api.dialogueRide(myAvatarId);
        if (d?.scene) { key = d.scene; title = d.title || title; }
      } catch { /* interview stands */ }
      if (!alive) return;
      const st = useWorldStore.getState();
      if (st.ride) return;
      st.closeChat();
      st.openScene({ key, title, quest: 'come_up_the_ride', room: null });
      st.startRide({ sceneKey: key, pace: INTRO_RIDE.pace });
    })();
    return () => { alive = false; };
  }, [chatAvatar?.avatarId, myAvatarId, rideOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null;

  return <Dock key={target.avatarId} target={target} explicit={!!chatAvatar} />;
}
